// src/components/hub/work/overlays/RepeatTaskModal.js
import React, { useState, useEffect } from "react";
import {
  popupBg,
  popupBorder,
  popupText,
  popupSubtext,
  popupBtn,
  popupInput,
} from "../constants";
import { recurringFromTask } from "../../../../api/hub/work";
import { useToast } from "../../../../contexts/ToastContext";

// "How often" cadence options — the label is what the user sees, the value is
// the cadence string the backend stores (fn_recurring_from_task.p_cadence).
// Hoisted to module scope so the arrays are NOT reallocated on every render
// (matches the PRIORITY_OPTIONS convention in constants.js).
const CADENCE_OPTIONS = [
  { value: "daily", label: "Every day" },
  { value: "weekly", label: "Every week" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Every month" },
  { value: "quarterly", label: "Every 3 months" },
  { value: "yearly", label: "Every year" },
];

const WEEKDAY_OPTIONS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

// Cadences that need a day-of-month picker (1–31). Weekly uses a weekday
// picker instead; daily/biweekly/yearly have no "On" field at all.
const DAY_OF_MONTH_CADENCES = new Set(["monthly", "quarterly"]);

const RepeatTaskModal = ({ open, onClose, task, onRepeatSet }) => {
  const { toastError } = useToast();
  const [cadence, setCadence] = useState("weekly");
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // When the modal opens (or the task changes), seed the day pickers from the
  // task's due date when one is available — weekly defaults to the due date's
  // weekday, monthly/quarterly to its day of the month. Falls back to Monday /
  // the 1st so the picker always has a sane value.
  useEffect(() => {
    if (!open) return;
    const due = task?.due ? new Date(task.due) : null;
    const validDue = due && !isNaN(due.getTime()) ? due : null;
    setCadence("weekly");
    setDayOfWeek(validDue ? validDue.getDay() : 1);
    setDayOfMonth(validDue ? validDue.getDate() : 1);
  }, [open, task]);

  if (!open) return null;

  const showDayOfWeek = cadence === "weekly";
  const showDayOfMonth = DAY_OF_MONTH_CADENCES.has(cadence);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!task?.id || submitting) return;
    setSubmitting(true);
    try {
      await recurringFromTask(
        task.id,
        cadence,
        showDayOfWeek ? dayOfWeek : null,
        showDayOfMonth ? dayOfMonth : null,
      );
      onClose();
      if (typeof onRepeatSet === "function") onRepeatSet();
    } catch (e) {
      // The backend raises a user-facing message (e.g. "this task already
      // repeats") — show it verbatim, no rewrite.
      toastError(e?.message || "Could not set up repeating task");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30"
      onClick={handleClose}
    >
      <div
        className={`relative ${popupBg} ${popupBorder} ${popupText} rounded-xl shadow-xl p-6 w-96`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className={`text-sm font-semibold ${popupText} mb-4`}>
          Make this task repeat
        </h3>

        {/* How often */}
        <div className="mb-4">
          <label className={`block text-xs ${popupSubtext} mb-1`}>
            How often
          </label>
          <select
            value={cadence}
            onChange={(e) => setCadence(e.target.value)}
            disabled={submitting}
            className={`w-full rounded-md ${popupInput} px-3 py-2 text-sm focus:border-blue-500 focus:outline-none`}
          >
            {CADENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* On — weekday picker for weekly, day-of-month for monthly/quarterly,
            hidden for daily/biweekly/yearly. */}
        {showDayOfWeek && (
          <div className="mb-4">
            <label className={`block text-xs ${popupSubtext} mb-1`}>
              On
            </label>
            <select
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(Number(e.target.value))}
              disabled={submitting}
              className={`w-full rounded-md ${popupInput} px-3 py-2 text-sm focus:border-blue-500 focus:outline-none`}
            >
              {WEEKDAY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {showDayOfMonth && (
          <div className="mb-4">
            <label className={`block text-xs ${popupSubtext} mb-1`}>
              On
            </label>
            <select
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(Number(e.target.value))}
              disabled={submitting}
              className={`w-full rounded-md ${popupInput} px-3 py-2 text-sm focus:border-blue-500 focus:outline-none`}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Note */}
        <p className={`text-xs ${popupSubtext} mb-4`}>
          Its subtasks are copied into every new copy.
        </p>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className={`px-3 py-1.5 text-sm rounded-lg ${popupBtn} border border-gray-200 dark:border-gray-700 disabled:opacity-40`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-1.5 text-sm rounded-lg bg-[#32B1FF] text-white hover:bg-[#1a9ce6] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Repeating...
              </>
            ) : (
              "Repeat"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RepeatTaskModal;
